import { Badge, Box, Button, Heading, Icon, Image, SimpleGrid, Stack, Text, useToast } from '@chakra-ui/react'
import CuideiAvailabilityPill from 'components/modules/cuidei/CuideiAvailabilityPill'
import CuideiLiveCard from 'components/modules/cuidei/CuideiLiveCard'
import CuideiShell from 'components/modules/cuidei/CuideiShell'
import { CUIDEI_EVENTS } from 'constants/cuidei'
import { useStartNewCuideiConsultation } from 'hooks/app/cuidei/useStartNewCuideiConsultation'
import { FC, useEffect } from 'react'
import { BsCameraVideo, BsCapsule, BsClock, BsFileEarmarkText, BsShieldCheck } from 'react-icons/bs'
import { setCuideiAttribution } from 'redux/slices/cuidei-slice'
import { useAppDispatch } from 'redux/store'
import { captureCuideiAttribution, trackCuideiEvent } from 'utils/cuidei'
import { isLiveServiceOpen, LIVE_SERVICE_WINDOW_MESSAGE } from 'utils/liveServiceWindow'

const benefits = [
  { icon: BsClock, label: 'Menos de 5 min de espera' },
  { icon: BsCameraVideo, label: 'Consulta imediata por vídeo' },
  { icon: BsShieldCheck, label: 'Pagamento seguro' },
  { icon: BsFileEarmarkText, label: 'Receita digital, se houver indicação' }
]

const howItWorks = [
  {
    step: '01',
    icon: BsFileEarmarkText,
    title: 'Informe seus dados',
    body: 'Preencha seus dados básicos para que o médico entenda seu perfil e conduza a consulta com segurança.'
  },
  {
    step: '02',
    icon: BsCameraVideo,
    title: 'Fale com o médico por vídeo',
    body: 'Você será direcionado para uma videochamada rápida com um médico, sem precisar agendar.'
  },
  {
    step: '03',
    icon: BsCapsule,
    title: 'Escolha como usar sua receita',
    body: 'Caso haja indicação médica, sua receita será disponibilizada e você escolhe como utilizá-la.'
  }
]

/** Cuidei landing page — QR-code entry point. Responsive (mobile + desktop), mirrors the Lovable mock. */
const CuideiLanding: FC = () => {
  const dispatch = useAppDispatch()
  const toast = useToast()
  const startNewConsultation = useStartNewCuideiConsultation()

  // Capture pharmacy attribution the moment the patient lands (spec §5).
  useEffect(() => {
    const attribution = captureCuideiAttribution()
    dispatch(setCuideiAttribution(attribution))
    trackCuideiEvent(CUIDEI_EVENTS.qrLandingView)
  }, [dispatch])

  const handleStart = () => {
    // No doctor 21h–9h BRT (ECS-650) — don't let the patient into the flow off-hours.
    if (!isLiveServiceOpen()) {
      toast({
        status: 'info',
        title: 'Atendimento indisponível agora',
        description: LIVE_SERVICE_WINDOW_MESSAGE,
        duration: 6000,
        isClosable: true
      })
      return
    }
    trackCuideiEvent(CUIDEI_EVENTS.landingCtaClicked)
    startNewConsultation()
  }

  const scrollToHowItWorks = () => {
    document.getElementById('como-funciona')?.scrollIntoView({ behavior: 'smooth' })
  }

  const headerCta = (
    <Button
      bg="cuidei.60"
      color="white"
      _hover={{ bg: 'cuidei.70' }}
      borderRadius="full"
      fontWeight={700}
      size="md"
      onClick={handleStart}
    >
      Começar agora →
    </Button>
  )

  return (
    <CuideiShell maxW="container.xl" headerAction={headerCta}>
      <Stack spacing={{ base: 12, lg: 24 }}>
        {/* Hero — two columns on desktop, stacked on mobile */}
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={{ base: 8, lg: 16 }} alignItems="center" pt={{ lg: 8 }}>
          <Stack spacing={6}>
            <Badge
              alignSelf="flex-start"
              bg="cuidei.20"
              color="cuidei.80"
              borderRadius="full"
              px={3}
              py={1.5}
              fontWeight={600}
              textTransform="none"
            >
              ⏱ Atendimento imediato · menos de 5 min de espera
            </Badge>

            <Heading
              as="h1"
              fontSize={{ base: '3xl', md: '5xl', lg: '6xl' }}
              lineHeight={1.1}
              color="cuidei.ink"
              letterSpacing="-0.02em"
            >
              Consulta médica por vídeo{' '}
              <Box as="span" color="cuidei.60">
                agora
              </Box>
              , em menos de 5 minutos.
            </Heading>

            <Stack direction={{ base: 'column', sm: 'row' }} spacing={3}>
              <Button
                size="lg"
                h={14}
                px={8}
                bg="cuidei.60"
                color="white"
                _hover={{ bg: 'cuidei.70' }}
                borderRadius="full"
                fontWeight={700}
                onClick={handleStart}
              >
                Iniciar minha consulta agora →
              </Button>
              <Button
                size="lg"
                h={14}
                px={8}
                variant="outline"
                bg="white"
                borderColor="cuidei.20"
                color="cuidei.ink"
                borderRadius="full"
                fontWeight={600}
                onClick={scrollToHowItWorks}
              >
                Como funciona
              </Button>
            </Stack>

            <SimpleGrid columns={{ base: 1, sm: 2 }} spacingX={8} spacingY={3} pt={2}>
              {benefits.map(({ icon, label }) => (
                <Stack key={label} direction="row" align="center" spacing={3}>
                  <Icon as={icon} color="cuidei.60" fontSize="lg" />
                  <Text color="cuidei.muted">{label}</Text>
                </Stack>
              ))}
            </SimpleGrid>
          </Stack>

          {/* Hero image card with floating availability pill */}
          <Box position="relative" w="full">
            <Box
              borderRadius="3xl"
              overflow="hidden"
              boxShadow="2xl"
              bg="cuidei.100"
              aspectRatio={{ base: '4 / 3', lg: '3 / 2' }}
            >
              <Image
                src="/landings/cuidei-hero-doctor.png"
                alt="Paciente em consulta médica por vídeo com um médico"
                objectFit="cover"
                w="full"
                h="full"
              />
            </Box>
            <CuideiAvailabilityPill />
          </Box>
        </SimpleGrid>

        {/* How it works */}
        <Stack spacing={{ base: 6, lg: 10 }} id="como-funciona">
          <Stack spacing={3} maxW="2xl">
            <Text fontSize="sm" fontWeight={700} color="cuidei.60" letterSpacing="0.08em">
              JORNADA SIMPLES
            </Text>
            <Heading as="h2" fontSize={{ base: '2xl', md: '4xl' }} color="cuidei.ink" letterSpacing="-0.02em">
              Consulta, receita e farmácia em uma jornada simples.
            </Heading>
            <Text color="cuidei.muted" fontSize={{ base: 'md', md: 'lg' }}>
              Tudo o que você precisa em 3 passos, com segurança e orientação médica de verdade.
            </Text>
          </Stack>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            {howItWorks.map(({ step, icon, title, body }) => (
              <Box
                key={step}
                bg="white"
                borderRadius="2xl"
                border="1px solid"
                borderColor="cuidei.20"
                p={{ base: 6, lg: 8 }}
              >
                <Stack spacing={5}>
                  <Stack direction="row" align="center" justify="space-between">
                    <Text fontSize="4xl" fontWeight={800} color="cuidei.30" lineHeight={1}>
                      {step}
                    </Text>
                    <Box
                      w={11}
                      h={11}
                      borderRadius="xl"
                      bg="cuidei.10"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Icon as={icon} color="cuidei.60" fontSize="lg" />
                    </Box>
                  </Stack>
                  <Stack spacing={2}>
                    <Text fontWeight={700} color="cuidei.ink" fontSize="lg">
                      {title}
                    </Text>
                    <Text fontSize="sm" color="cuidei.muted">
                      {body}
                    </Text>
                  </Stack>
                </Stack>
              </Box>
            ))}
          </SimpleGrid>
        </Stack>

        {/* Live preview / availability section — full-bleed gradient, abuts the
            footer. Promotional copy (visitor hasn't started yet) — NOT the
            post-payment waiting copy used in StepFilaVideo. */}
        <CuideiLiveCard
          title="Médico disponível agora"
          description="Assim que você iniciar, conectamos você a um médico por vídeo em menos de 5 minutos — sem agendamento."
          statusLabel="Médicos online agora"
          mb={{ base: -6, md: -10 }}
        />
      </Stack>
    </CuideiShell>
  )
}

export default CuideiLanding
